import {
	CreditCardIcon,
	GiftIcon,
	PhoneIcon,
	QuestionMarkCircleIcon,
	TicketIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { Localized, useLocalization } from "@launerlondon/l10n";
import {
	Currency,
	Total,
	allowAfterpayPayment,
	allowKlarnaPayment,
	balanceWithAmounts,
} from "@launerlondon/shared";
import { CheckoutLoaderData } from "@launerlondon/shop-router";
import type {
	CardPaymentData,
	PaymentData,
	PaymentMethod,
	RootState,
} from "@launerlondon/shop-types";
import { useStripe } from "@stripe/react-stripe-js";
import cx from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoaderData, useSubmit } from "react-router";
import CartPaymentMethodCard from "../components/CartPaymentMethodCard";
import { getPaymentRequestType } from "../lib/stripe";
import { setPaymentMethod } from "../redux/actions";

type Props = {
	payment?: PaymentData;
	total?: Total;
	onChange(payment?: PaymentData): void;
};

const klarnaPath =
	"M111.153 65.031a2.81 2.81 0 0 0-1.094.227 2.852 2.852 0 0 0-.925.637 2.932 2.932 0 0 0-.827 2.07 2.954 2.954 0 0 0 .827 2.07c.264.272.579.489.925.637a2.81 2.81 0 0 0 1.094.227c1.57 0 2.847-1.314 2.847-2.934a2.973 2.973 0 0 0-.212-1.119 2.929 2.929 0 0 0-.615-.95 2.856 2.856 0 0 0-.926-.638 2.803 2.803 0 0 0-1.094-.227Zm-9.368-2.266c0-2.22-1.84-4.016-4.11-4.016-2.27 0-4.11 1.8-4.11 4.016 0 2.217 1.843 4.014 4.113 4.014 2.27 0 4.107-1.797 4.107-4.014Zm.016-7.806h4.535v15.61h-4.535v-.998A7.79 7.79 0 0 1 97.304 71c-4.414 0-7.992-3.686-7.992-8.235 0-4.548 3.578-8.234 7.992-8.234 1.67 0 3.216.526 4.497 1.428v-1Zm-36.296 2.034V54.96H60.86v15.61h4.655v-7.288c0-2.46 2.584-3.78 4.379-3.78h.053v-4.54c-1.842 0-3.533.813-4.443 2.032Zm-11.569 5.772c0-2.22-1.84-4.016-4.11-4.016-2.27 0-4.11 1.8-4.11 4.016 0 2.217 1.843 4.014 4.113 4.014 2.27 0 4.107-1.797 4.107-4.014Zm.017-7.806h4.536v15.61h-4.536v-.998A7.795 7.795 0 0 1 49.455 71c-4.414 0-7.992-3.686-7.992-8.235 0-4.548 3.578-8.234 7.992-8.234a7.78 7.78 0 0 1 4.498 1.428v-1Zm27.3-.418c-1.811 0-3.526.579-4.674 2.176v-1.756h-4.516V70.57h4.572v-8.202c0-2.372 1.544-3.536 3.405-3.536 1.992 0 3.14 1.227 3.14 3.503v8.235h4.53v-9.925c0-3.632-2.804-6.103-6.458-6.103ZM34.855 70.57h4.748V48.005h-4.748V70.57ZM14 70.575h5.028V48h-5.025L14 70.575ZM31.587 48c0 4.886-1.847 9.434-5.143 12.813l6.949 9.762h-6.209l-7.55-10.607 1.95-1.506a12.945 12.945 0 0 0 3.755-4.616A13.228 13.228 0 0 0 26.663 48h4.925Z";
const applepayPath =
	"M41.8 60.393c.055 4.95 4.461 6.595 4.497 6.614-.036.11-.707 2.356-2.32 4.658-1.397 1.992-2.847 3.983-5.132 4.02-2.248.036-2.974-1.297-5.53-1.297-2.575 0-3.373 1.26-5.494 1.333-2.213.073-3.88-2.156-5.295-4.147-2.883-4.056-5.077-11.474-2.122-16.48 1.469-2.484 4.098-4.055 6.945-4.11 2.158-.037 4.207 1.425 5.53 1.425 1.324 0 3.808-1.754 6.42-1.498 1.087.036 4.17.438 6.128 3.252-.163.091-3.663 2.083-3.626 6.23Zm-4.224-12.168c1.178-1.388 1.958-3.307 1.74-5.225-1.686.073-3.735 1.096-4.932 2.485-1.087 1.224-2.03 3.179-1.777 5.06 1.886.147 3.808-.931 4.969-2.32Zm16.963-2.372a65.69 65.69 0 0 1 2.88-.398c1.046-.126 2.202-.18 3.45-.18 1.797 0 3.338.217 4.641.632 1.303.433 2.367 1.03 3.21 1.842a7.701 7.701 0 0 1 1.688 2.51c.404.975.606 2.094.606 3.358 0 1.535-.275 2.871-.826 4.027a8.225 8.225 0 0 1-2.256 2.889c-.954.776-2.092 1.354-3.413 1.752-1.32.379-2.752.577-4.311.577-1.413 0-2.587-.108-3.541-.307v12.712h-2.11V45.853h-.018Zm2.11 14.842a9.242 9.242 0 0 0 1.706.307c.624.054 1.302.09 2.018.09 2.697 0 4.77-.613 6.256-1.86 1.486-1.227 2.22-3.05 2.22-5.434 0-1.156-.202-2.149-.587-2.998a5.358 5.358 0 0 0-1.651-2.094c-.716-.542-1.578-.957-2.55-1.228-.991-.271-2.074-.415-3.285-.415-.953 0-1.779.036-2.476.108a22 22 0 0 0-1.651.235v13.29Zm31.133 9.534c0 .85.019 1.716.037 2.564.037.85.128 1.68.275 2.474h-1.981L85.8 72.27h-.092a6.89 6.89 0 0 1-1.027 1.228 7.48 7.48 0 0 1-1.45 1.12 7.157 7.157 0 0 1-1.89.812 8.413 8.413 0 0 1-2.31.307c-1.047 0-1.964-.163-2.753-.506-.789-.343-1.45-.776-1.944-1.318a5.087 5.087 0 0 1-1.138-1.878 6.132 6.132 0 0 1-.385-2.112c0-2.51 1.064-4.424 3.21-5.779 2.147-1.354 5.357-2.004 9.669-1.95v-.578c0-.56-.055-1.191-.165-1.913a5.958 5.958 0 0 0-.697-2.059c-.367-.65-.9-1.192-1.615-1.625-.715-.433-1.688-.668-2.917-.668-.935 0-1.853.144-2.752.415a9.014 9.014 0 0 0-2.495 1.174l-.679-1.553a10.2 10.2 0 0 1 2.954-1.39 12.257 12.257 0 0 1 3.192-.416c1.505 0 2.715.253 3.651.758.936.506 1.688 1.156 2.22 1.95a7.38 7.38 0 0 1 1.1 2.673 14.6 14.6 0 0 1 .294 2.943v8.324Zm-2.11-6.301a29.946 29.946 0 0 0-3.559.126c-1.247.126-2.385.38-3.43.776-1.046.398-1.909.976-2.606 1.752-.678.758-1.027 1.788-1.027 3.052 0 1.498.44 2.6 1.303 3.304.862.704 1.834 1.065 2.917 1.065.862 0 1.65-.126 2.33-.36a6.288 6.288 0 0 0 1.798-.958 6.011 6.011 0 0 0 1.284-1.354 6.21 6.21 0 0 0 .789-1.553c.147-.596.22-1.011.22-1.282l-.019-4.568Zm6.22-9.86 5.467 13.778a59.411 59.411 0 0 1 1.578 4.604h.092c.201-.65.44-1.372.715-2.184.275-.813.569-1.644.899-2.492l5.119-13.687H108l-6.238 15.546a168.109 168.109 0 0 1-1.78 4.479 43.316 43.316 0 0 1-1.687 3.63 25.227 25.227 0 0 1-1.725 2.852c-.587.83-1.23 1.553-1.944 2.167-.844.74-1.615 1.282-2.312 1.607-.697.343-1.174.56-1.413.632l-.715-1.716a19.346 19.346 0 0 0 1.798-.92c.66-.38 1.302-.885 1.926-1.5.532-.523 1.138-1.227 1.78-2.094.642-.867 1.21-1.914 1.724-3.16.184-.47.275-.776.275-.92 0-.2-.091-.524-.275-.921l-7.76-19.646h2.238v-.054Z";
const alipayPath =
	"M43.935 67.365a15.296 15.296 0 0 0-7.664 2.849l.044-.032c-6.005 5.219-2.411 14.766 9.74 14.766a26.247 26.247 0 0 0 19.601-11.615l.06-.096c-6.023-3.69-13.318-5.875-21.122-5.875-.232 0-.466.003-.698.005h.034l.005-.002Zm33.33 2.27c2.756-4.726 4.993-10.198 6.428-15.987l.09-.432H68.389v-5.529h18.86v-3.085h-18.86v-9.22h-7.74a1.31 1.31 0 0 0-1.307 1.308v7.914H40.268v3.086h19.073v5.529H43.594v3.086h30.544c-1.206 4.104-2.7 7.659-4.542 11l.141-.279c-9.909-3.26-20.487-5.904-27.13-4.276-3.432.69-6.383 2.422-8.584 4.846l-.01.013c-7.378 8.954-2.086 22.55 13.495 22.55a33.076 33.076 0 0 0 24.906-13.472l.065-.09c10.26 4.919 30.573 13.364 30.573 13.364v.51c-.008 6.891-5.594 12.474-12.487 12.474H37.441c-6.894 0-12.485-5.583-12.493-12.476V37.542c.008-6.894 5.596-12.477 12.49-12.477h53.125c6.893 0 12.481 5.586 12.486 12.477v40.382c-5.304-1.049-9.927-2.416-14.364-4.153l.568.195c-3.125-1.044-7.318-2.64-11.99-4.325l.003-.006Z";
const wechatPath =
	"M93.882 72.686a4.137 4.137 0 0 0 4.082-4.082 4.067 4.067 0 0 0-4.082-4.081 4.067 4.067 0 0 0-4.082 4.082 4.064 4.064 0 0 0 4.082 4.081Zm-20.114 0a4.136 4.136 0 0 0 4.082-4.082 4.07 4.07 0 0 0-4.082-4.081 4.064 4.064 0 0 0-4.082 4.082 4.061 4.061 0 0 0 4.082 4.081Zm29.85 22.91a1.576 1.576 0 0 0-.782 1.681c0 .219 0 .441.114.668.445 1.896 1.336 4.914 1.336 5.028 0 .332.114.554.114.782a.989.989 0 0 1-.295.708 1.002 1.002 0 0 1-.71.292c-.227 0-.336-.11-.559-.219l-6.586-3.8a3.636 3.636 0 0 0-1.564-.445c-.332 0-.668 0-.89.109-3.128.895-6.364 1.341-9.823 1.341-16.637 0-30.032-11.168-30.032-25.023 0-13.85 13.395-25.023 30.032-25.023 16.632 0 30.027 11.173 30.027 25.023 0 7.486-4.018 14.3-10.382 18.882v-.004ZM85.241 47.177a41.543 41.543 0 0 0-1.273-.023c-18.972 0-34.573 13-34.573 29.569 0 2.518.364 4.954 1.037 7.273h-.405a45.292 45.292 0 0 1-11.745-1.673c-.336-.114-.673-.114-1.01-.114a3.782 3.782 0 0 0-1.9.56l-7.945 4.567c-.227.114-.45.228-.672.228a1.24 1.24 0 0 1-1.228-1.228c0-.336.11-.559.223-.895.11-.11 1.118-3.791 1.677-6.018 0-.228.11-.56.11-.782a2.529 2.529 0 0 0-1.005-2.01C18.809 71.165 14 63.028 14 53.992 14 37.386 30.227 24 50.136 24c17.114 0 31.5 9.86 35.105 23.173v.004Zm-23.427 1.9c2.604 0 4.663-2.168 4.663-4.663a4.627 4.627 0 0 0-4.663-4.664 4.627 4.627 0 0 0-4.664 4.664 4.627 4.627 0 0 0 4.664 4.663Zm-23.91 0c2.605 0 4.669-2.168 4.669-4.663a4.631 4.631 0 0 0-4.669-4.664 4.63 4.63 0 0 0-4.663 4.664 4.63 4.63 0 0 0 4.663 4.663Z";
const afterpayPath =
	"m90.637 47.504-8.614-5.113-8.736-5.186c-5.79-3.437-13.008.888-13.008 7.794v1.165c0 .313.08.62.231.89.152.271.37.496.632.652l4.053 2.405c1.124.668 2.526-.172 2.526-1.513v-2.757c0-1.365 1.426-2.22 2.571-1.54l7.984 4.74 7.96 4.725c1.15.682 1.15 2.398 0 3.08l-7.96 4.726-7.984 4.74c-1.145.679-2.571-.175-2.571-1.54v-1.37c0-6.907-7.218-11.232-13.008-7.795l-8.736 5.186-8.614 5.114c-5.817 3.453-5.817 12.135 0 15.589l8.614 5.113 8.736 5.186c5.79 3.437 13.008-.888 13.008-7.795v-1.166c0-.313-.08-.62-.231-.891a1.749 1.749 0 0 0-.632-.651l-4.053-2.406c-1.124-.668-2.526.172-2.526 1.513v2.76c0 1.365-1.427 2.22-2.57 1.54l-7.985-4.74-7.96-4.725c-1.15-.682-1.15-2.398 0-3.08l7.96-4.726 7.984-4.74c1.145-.68 2.571.175 2.571 1.54v1.37c0 6.907 7.218 11.232 13.008 7.794l8.736-5.185 8.614-5.114c5.817-3.453 5.817-12.135 0-15.589";

const SVG: React.FC<{ path: string }> = ({ path }) => {
	return <svg viewBox="0 0 128 128" children={<path d={path} />} />;
};

const PaymentIcon: React.FC<{
	active: boolean;
	type: Exclude<PaymentMethod, "voucher">;
	onClick(): void;
}> = (props) => {
	const { l10n } = useLocalization();
	const Icon = {
		card: <CreditCardIcon className="h-10 w-10" />,
		offline: <PhoneIcon className="h-10 w-10" />,
		alipay: <SVG path={alipayPath} />,
		wechat: <SVG path={wechatPath} />,
		apple: <SVG path={applepayPath} />,
		google: (
			<svg viewBox="0 0 128 128">
				<path
					fill="#000"
					d="M30.379 50.484a8.901 8.901 0 0 1 6.283 2.462l4.674-4.67a15.735 15.735 0 0 0-10.961-4.268 16.382 16.382 0 0 0-14.63 9.012h-.005a16.415 16.415 0 0 0 0 14.713l.005-.004a16.383 16.383 0 0 0 14.634 9.02c4.416 0 8.13-1.452 10.833-3.948h.01c3.077-2.86 4.846-7.075 4.846-12.057 0-1.155-.103-2.307-.308-3.443H30.37v6.316h8.86a7.591 7.591 0 0 1-3.282 5.08v.003a9.924 9.924 0 0 1-14.768-5.188l.002-.002a9.876 9.876 0 0 1-.513-3.135c0-1.06.172-2.112.507-3.117l.002.002a9.801 9.801 0 0 1 9.2-6.776Z"
				/>
				<path
					fill="#000"
					d="M66.727 63.063a8.762 8.762 0 0 0 6.291-2.43 8.236 8.236 0 0 0 0-12.086 8.779 8.779 0 0 0-6.291-2.462h-9.73v28.567h3.673v-11.59h6.057Zm.099-13.445a5.093 5.093 0 0 1 5.105 4.97 4.813 4.813 0 0 1-1.474 3.5 4.9 4.9 0 0 1-3.631 1.474H60.67v-9.944h6.156Zm18.434 4.839c2.714 0 4.855.726 6.423 2.179 1.568 1.453 2.351 3.442 2.351 5.967v12.045h-3.5v-2.717h-.152c-1.51 2.237-3.538 3.345-6.062 3.345a7.867 7.867 0 0 1-5.412-1.912 6.106 6.106 0 0 1-2.188-4.778 5.774 5.774 0 0 1 2.294-4.817c1.53-1.207 3.575-1.798 6.12-1.798 2.178 0 3.976.41 5.371 1.207v-.821a4.165 4.165 0 0 0-1.51-3.25 5.183 5.183 0 0 0-3.558-1.338 5.578 5.578 0 0 0-4.843 2.565l-3.23-2.028c1.779-2.572 4.41-3.855 7.896-3.85Zm-4.74 14.187a2.892 2.892 0 0 0 1.231 2.388 4.514 4.514 0 0 0 2.849.957 5.823 5.823 0 0 0 4.128-1.72 5.4 5.4 0 0 0 1.818-4.034c-1.149-.899-2.733-1.358-4.78-1.358a6.275 6.275 0 0 0-3.731 1.07c-1.018.748-1.519 1.646-1.519 2.697h.004Z"
				/>
				<path
					fill="#000"
					d="m113.996 55.089-12.242 28.144H97.97l4.551-9.849-8.068-18.3h4.018l5.811 14.036h.078l5.659-14.035 3.977.004Z"
				/>
			</svg>
		),
		microsoft: <QuestionMarkCircleIcon className="h-10 w-10" />,
		klarna: <SVG path={klarnaPath} />,
		afterpay_clearpay: <SVG path={afterpayPath} />,
	}[props.type];
	return (
		<label
			className={cx(
				"relative h-16 w-16",
				"flex items-center justify-center",
				"cursor-pointer rounded",
				props.active
					? "bg-black fill-white"
					: "bg-gray-100 fill-gray-700",
			)}
			title={l10n.getString(`checkout-payment-type--${props.type}`)}
			onClick={props.onClick}
		>
			<>{Icon}</>
			<input
				className="absolute inset-0 h-0 w-0 opacity-0"
				type="radio"
				name="payment-method"
				required
			/>
		</label>
	);
};

const CartPaymentMethod: React.FC<Props> = ({ payment, onChange, total }) => {
	const stripe = useStripe();
	const dispatch = useDispatch();
	const { vouchers } = useLoaderData() as CheckoutLoaderData;
	const submit = useSubmit();

	const bVouchers = balanceWithAmounts(vouchers, total?.gross || { gbp: 0 });

	const locale = useSelector((state: RootState) => state.locale);
	const allowCNPayment = useMemo(
		() =>
			[locale.country, locale.user?.country].includes("CN") ||
			[locale.currency, locale.user?.country].includes("CNY") ||
			navigator.languages.some((l) => l.startsWith("cn")),
		[locale],
	);

	const [data, setData] = useState<PaymentData | undefined>(payment);
	const [vendorPayment, setVendorPayment] = useState<
		"apple" | "google" | "microsoft"
	>();
	const [selectorHidden, setSelectorHidden] = useState(false);

	const paymentCurrency = locale.billingCurrency;
	const currency = paymentCurrency.toLowerCase() as Currency;
	const discount = total?.discount[currency] || 0;
	const gross = total?.gross[currency] || 0;

	useEffect(() => {
		setSelectorHidden(
			["card", "offline", "voucher"].includes(data?.type || ""),
		);
		onChange(data);
		dispatch(setPaymentMethod(data?.type));
	}, [data, onChange, dispatch, payment]);

	useEffect(() => {
		if (!stripe) return;
		getPaymentRequestType(stripe).then(setVendorPayment);
	}, [stripe]);

	useEffect(() => {
		setData(
			discount >= gross
				? { type: "voucher", currency: paymentCurrency }
				: undefined,
		);
	}, [gross, discount]);

	return (
		<div className="mb-4 border-b pb-4">
			<h3 className="mb-2 text-[14px]">
				<span className="uppercase tracking-wide text-gray-800">
					<Localized id="checkout-payment-title" />:{" "}
				</span>
				<Localized id={`checkout-payment-type--${data?.type}`} />{" "}
				{selectorHidden && data?.type !== "voucher" && (
					<a
						className="block w-max text-xs underline"
						onClick={() => setData(undefined)}
					>
						<Localized id="checkout-payment-reset" />
					</a>
				)}
			</h3>
			{!selectorHidden && (
				<div className="flex items-center gap-4">
					{[
						allowCNPayment && "alipay",
						allowCNPayment && "wechat",
						vendorPayment,
						"card",
						allowKlarnaPayment(locale.country) && "klarna",
						allowAfterpayPayment(locale.country, total?.gross) &&
							"afterpay_clearpay",
						"offline",
					]
						.filter((v): v is Exclude<PaymentMethod, "voucher"> =>
							Boolean(v),
						)
						.map((type) => (
							<PaymentIcon
								key={type}
								type={type}
								active={type === data?.type}
								onClick={() => {
									if (type === "alipay") {
										setData({
											type: "alipay",
											currency: "CNY",
										});
									} else if (type === "afterpay_clearpay") {
										setData({
											type: "afterpay_clearpay",
											currency: "GBP",
										});
									} else if (type === "card") {
										setData({
											type,
											currency: paymentCurrency,
										});
									} else {
										setData({
											type,
											currency: [
												"GBP",
												"EUR",
												"USD",
											].includes(paymentCurrency)
												? paymentCurrency
												: "GBP",
										});
									}
								}}
							/>
						))}
				</div>
			)}
			{data?.type === "offline" && (
				<aside>
					<p className="text-sm">
						<Localized id="checkout-payment-text--offline" html />
					</p>
				</aside>
			)}
			{data?.type === "card" && (
				<CartPaymentMethodCard
					payment={data as CardPaymentData}
					onChange={setData}
				/>
			)}
			{bVouchers.length > 0 && (
				<div className="my-4 space-y-2">
					<h3 className="text-sm font-medium">Vouchers used</h3>
					<div className="flex gap-10">
						{bVouchers.map((v) => (
							<div
								key={v.id}
								className="relative flex rounded bg-gray-100"
							>
								{v.type === "coupon" ? (
									<TicketIcon className="ml-2 h-5 w-5 self-center" />
								) : (
									<GiftIcon className="ml-2 h-5 w-5 self-center" />
								)}
								<h3 className="p-2 text-sm font-medium">
									{v.amount} {v.currency}
								</h3>
								{v.residual != undefined && (
									<h4 className="absolute -bottom-5 w-full text-center text-xs">
										{
											v.residual[
												v.currency.toLowerCase() as "gbp"
											]
										}{" "}
										{v.currency} Left
									</h4>
								)}
								<button
									type="button"
									className="rounded-r bg-gray-300 px-2"
									onClick={() =>
										submit(
											{
												id: v.id!,
												action: "remove_voucher",
											},
											{ method: "delete" },
										)
									}
								>
									<XMarkIcon className="h-4 w-4" />
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default CartPaymentMethod;
